/**
 * ForgotPassword page for Wadakaroo
 *
 * Logged-out flow: the user enters their email and we ask the backend to send a
 * reset link. The backend always responds generically (it never reveals whether
 * an account exists), so we always show the same confirmation.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { forgotPasswordApi } from '../services/api';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setIsLoading(true);
        setError(null);
        try {
            await forgotPasswordApi(email.trim());
            setSent(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6 py-12">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-10 justify-center">
                    <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-display font-bold text-lg">W</span>
                    </div>
                    <span className="text-gray-900 font-display font-bold text-2xl tracking-tight">wadakaroo</span>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                    {sent ? (
                        <div className="text-center">
                            <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="text-emerald-600" size={24} />
                            </div>
                            <h2 className="text-xl font-display font-bold text-gray-900 mb-2">Check your email</h2>
                            <p className="text-gray-500 text-sm mb-6">
                                If an account exists for <span className="font-medium text-gray-700">{email}</span>,
                                we've sent a link to reset your password. The link expires in 1 hour.
                            </p>
                            <Link
                                to="/login"
                                className="inline-flex items-center gap-2 text-sm font-medium text-emerald-600 hover:text-emerald-700"
                            >
                                <ArrowLeft size={16} /> Back to sign in
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="mb-6">
                                <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">Forgot password?</h2>
                                <p className="text-gray-500 text-sm">
                                    Enter your email and we'll send you a link to reset your password.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {error && (
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                        <p className="text-red-700 text-sm">{error}</p>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Email address
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-gray-900 placeholder-gray-400"
                                            placeholder="you@company.com"
                                            required
                                            autoComplete="email"
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
                                >
                                    {isLoading ? <Loader2 size={18} className="animate-spin" /> : null}
                                    Send reset link
                                </button>
                            </form>

                            <div className="mt-6 text-center">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                                >
                                    <ArrowLeft size={16} /> Back to sign in
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
